import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["option", "checkbox", "kindSelect", "scopeSelect", "settings"]
  static values = {
    variantLabel: { type: String, default: "Variant Attributes" },
    infoLabel: { type: String, default: "Info Attributes" },
    internalLabel: { type: String, default: "Internal Attributes" },
    selectPlaceholder: { type: String, default: "Select..." },
    noAttributes: { type: String, default: "No attributes selected" },
    yesLabel: { type: String, default: "Yes" },
    noLabel: { type: String, default: "No" }
  }

  connect() {
    this.form = this.element.closest('form')
    this.attributeValueCache = {}

    // Listen for new variants being added
    this.element.addEventListener('variant:added', (event) => {
      this.updateNewVariantOptions(event.detail.panel)
    })

    this.updateVariantFields()
  }

  toggleOption(event) {
    const checkbox = event.target
    const optionRow = checkbox.closest('[data-product-options-target="option"]')
    const settingsContainer = optionRow.querySelector('[data-product-options-target="settings"]')

    if (checkbox.checked) {
      settingsContainer?.classList.remove('hidden')
    } else {
      settingsContainer?.classList.add('hidden')
    }

    // Update variant fields
    this.updateVariantFields()
  }

  getSelectedOptions() {
    const options = []

    this.optionTargets.forEach(optionEl => {
      const checkbox = optionEl.querySelector('[data-product-options-target="checkbox"]')
      const kindSelect = optionEl.querySelector('[data-product-options-target="kindSelect"]')
      const scopeSelect = optionEl.querySelector('[data-product-options-target="scopeSelect"]')

      if (checkbox && checkbox.checked) {
        options.push({
          id: optionEl.dataset.optionId,
          name: optionEl.dataset.optionName,
          field_type: optionEl.dataset.optionType,
          kind: kindSelect ? kindSelect.value : 'info',
          scope: scopeSelect ? scopeSelect.value : 'per_variant',
          select_options: JSON.parse(optionEl.dataset.selectOptions || '[]')
        })
      }
    })

    return options
  }

  updateVariantFields() {
    if (!this.form) return

    const selectedOptions = this.getSelectedOptions()

    // Find all variant option containers in variant fields
    const variantContainers = this.form.querySelectorAll('[data-variant-options-container]')

    variantContainers.forEach(container => {
      const variantIndex = container.dataset.variantIndex

      // Collect existing values before updating
      const existingValues = this.collectExistingValues(container)

      // Render new options
      container.innerHTML = this.renderOptionsContainer(selectedOptions, variantIndex, existingValues)
    })

    this.element.dispatchEvent(new CustomEvent('variant:labels-changed', { bubbles: true }))
  }

  collectExistingValues(container) {
    const values = {}
    const variantIndex = String(container.dataset.variantIndex ?? '0')

    container.querySelectorAll('input[name*="[attribute_definition_id]"]').forEach(hiddenInput => {
      const optionId = hiddenInput.value
      const parent = hiddenInput.closest('div')
      const valueInput = parent?.querySelector('input[name*="[value]"], select[name*="[value]"]')

      if (valueInput && optionId) {
        if (valueInput.type === 'checkbox') {
          values[optionId] = valueInput.checked ? 'true' : ''
        } else {
          values[optionId] = valueInput.value
        }

        const idInput = parent?.querySelector('input[name*="[id]"]')
        const cached = this.getCachedAttributeValue(variantIndex, optionId)
        const recordId = idInput?.value || cached.id || ''

        if (recordId) {
          values[`${optionId}_id`] = recordId
        }

        this.setCachedAttributeValue(variantIndex, optionId, values[optionId], recordId)
      }
    })

    return values
  }

  renderOptionsContainer(attributes, variantIndex, existingValues = {}) {
    if (attributes.length === 0) {
      return `<p class="text-xs text-slate-500 dark:text-slate-500 italic">${this.escapeHtml(this.noAttributesValue)}</p>`
    }

    const variantAttrs = attributes.filter(a => a.kind === 'variant')
    const infoAttrs = attributes.filter(a => a.kind === 'info')
    const internalAttrs = attributes.filter(a => a.kind === 'internal')

    let html = ''

    if (variantAttrs.length > 0) {
      html += `
        <div class="mb-3">
          <h5 class="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">${this.variantLabelValue}</h5>
          <div class="grid grid-cols-3 gap-3">
            ${variantAttrs.map(attr => this.renderOptionField(attr, variantIndex, existingValues, true)).join('')}
          </div>
        </div>
      `
    }

    if (infoAttrs.length > 0) {
      html += `
        <div class="mb-3">
          <h5 class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">${this.infoLabelValue}</h5>
          <div class="grid grid-cols-3 gap-3">
            ${infoAttrs.map(attr => this.renderOptionField(attr, variantIndex, existingValues, false)).join('')}
          </div>
        </div>
      `
    }

    if (internalAttrs.length > 0) {
      html += `
        <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
          <h5 class="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-2">${this.internalLabelValue}</h5>
          <div class="grid grid-cols-3 gap-3">
            ${internalAttrs.map(attr => this.renderOptionField(attr, variantIndex, existingValues, false)).join('')}
          </div>
        </div>
      `
    }

    return html
  }

  updateNewVariantOptions(panel) {
    if (!panel) return

    const container = panel.querySelector('[data-variant-options-container]')
    if (!container) return

    const variantIndex = container.dataset.variantIndex
    const selectedOptions = this.getSelectedOptions()

    container.innerHTML = this.renderOptionsContainer(selectedOptions, variantIndex)
  }

  renderOptionField(attr, variantIndex, existingValues = {}, variantAttribute = false) {
    const variantKey = String(variantIndex ?? '0')
    const cached = this.getCachedAttributeValue(variantKey, attr.id)
    const existingValue = existingValues[attr.id] ?? cached.value ?? ''
    const existingRecordId = existingValues[`${attr.id}_id`] || cached.id || ''

    // Use existing record ID as key if available, otherwise generate a new timestamp
    const recordKey = existingRecordId || (Date.now() + Math.random().toString(36).slice(2, 11))
    const safeVariantIndex = variantIndex || variantIndex === 0 ? variantIndex : '0'
    const baseName = `product[variants_attributes][${safeVariantIndex}][variant_attribute_values_attributes][${recordKey}]`
    const baseId = `product_variants_attributes_${variantIndex}_variant_attribute_values_attributes_${recordKey}`
    const fieldName = `${baseName}[value]`
    const fieldId = `${baseId}_value`
    const hiddenFieldName = `${baseName}[attribute_definition_id]`

    const labelClass = "block text-xs font-medium text-slate-700 dark:text-slate-400"
    const inputClass = "appearance-none block w-full px-2 py-1.5 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 dark:bg-slate-600 dark:text-white text-xs"

    const idField = existingRecordId ? `<input type="hidden" name="${baseName}[id]" value="${existingRecordId}">` : ''
    const hiddenField = `<input type="hidden" name="${hiddenFieldName}" value="${attr.id}">`
    let fieldHtml = ''

    switch (attr.field_type) {
      case 'text':
        fieldHtml = `<input type="text" name="${fieldName}" id="${fieldId}" class="${inputClass}" placeholder="${attr.name}" value="${this.escapeHtml(existingValue)}">`
        break
      case 'number':
        fieldHtml = `<input type="number" name="${fieldName}" id="${fieldId}" class="${inputClass}" placeholder="${attr.name}" value="${this.escapeHtml(existingValue)}">`
        break
      case 'boolean':
        const selected1 = existingValue === '' ? 'selected' : ''
        const selected2 = existingValue === 'true' ? 'selected' : ''
        const selected3 = existingValue === 'false' ? 'selected' : ''
        fieldHtml = `<select name="${fieldName}" id="${fieldId}" class="${inputClass}"><option value="" ${selected1}></option><option value="true" ${selected2}>${this.escapeHtml(this.yesLabelValue)}</option><option value="false" ${selected3}>${this.escapeHtml(this.noLabelValue)}</option></select>`
        break
      case 'select':
        const selectOptions = attr.select_options.map(opt => {
          const selected = opt === existingValue ? 'selected' : ''
          return `<option value="${opt}" ${selected}>${opt}</option>`
        }).join('')
        fieldHtml = `<select name="${fieldName}" id="${fieldId}" class="${inputClass}"><option value="">${this.selectPlaceholderValue}</option>${selectOptions}</select>`
        break
      default:
        fieldHtml = `<input type="text" name="${fieldName}" id="${fieldId}" class="${inputClass}" placeholder="${attr.name}" value="${this.escapeHtml(existingValue)}">`
    }

    return `
      <div ${variantAttribute ? `data-variant-attribute-field="true" data-variant-attribute-id="${this.escapeHtml(attr.id)}" data-variant-attribute-name="${this.escapeHtml(attr.name)}"` : ''}>
        ${idField}
        ${hiddenField}
        <label for="${fieldId}" class="${labelClass}">${attr.name}</label>
        <div class="mt-0.5">${fieldHtml}</div>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getCachedAttributeValue(variantIndex, attributeId) {
    return this.attributeValueCache?.[variantIndex]?.[attributeId] || {}
  }

  setCachedAttributeValue(variantIndex, attributeId, value, id) {
    if (!this.attributeValueCache[variantIndex]) {
      this.attributeValueCache[variantIndex] = {}
    }

    this.attributeValueCache[variantIndex][attributeId] = {
      value: value ?? '',
      id: id || this.attributeValueCache[variantIndex][attributeId]?.id || ''
    }
  }
}
